import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import {
  getAllCategories,
  createCategory,
  deleteCategory,
  updateCategory,
} from '../services/categoryService';
import swal from 'sweetalert';
import { FiUpload } from 'react-icons/fi';
import { importCategoriesFromExcel } from '../services/categoryService';


const CATEGORY_CACHE_KEY = 'cached_categories';

const CategoriesScreen = () => {
  const { slug } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState({ inserted: 0, skipped: 0 });
  const [isRefreshing, setIsRefreshing] = useState(false);


const fetchCategories = async (showRefreshLoader = false) => {
  try {
    if (showRefreshLoader) setIsRefreshing(true);

    const res = await getAllCategories();
    const newCategories = res.data;
    const cachedRaw = localStorage.getItem(CATEGORY_CACHE_KEY);
    const cachedCategories = cachedRaw ? JSON.parse(cachedRaw) : [];

    const hasChanged = JSON.stringify(cachedCategories) !== JSON.stringify(newCategories);
    if (hasChanged) {
      setCategories(newCategories);
      localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(newCategories));
    } else if (!cachedRaw) {
      setCategories(newCategories);
    }
  } catch {
    swal("Erreur", "Échec du chargement des catégories", "error");
  } finally {
    if (showRefreshLoader) {
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  }
};


  const handleSubmit = async () => {
    try {
      if (editMode) {
        await updateCategory(editingId, categoryName);
        swal("Modifié", "Catégorie mise à jour.", "success");
      } else {
        await createCategory(categoryName);
        swal("Ajoutée", "Catégorie ajoutée avec succès.", "success");
      }

      const res = await getAllCategories();
      setCategories(res.data);
      localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(res.data));

      setShowModal(false);
      setEditMode(false);
      setCategoryName('');
      setEditingId(null);
    } catch {
      swal("Erreur", "Nom déjà utilisé ou erreur serveur", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Supprimer cette catégorie ?",
      text: "Cette action est irréversible.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (confirm) {
      try {
        await deleteCategory(id);
        swal("Supprimée", "Catégorie supprimée.", "success");

        const res = await getAllCategories();
        setCategories(res.data);
        localStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify(res.data));
      } catch {
        swal("Erreur", "Échec de la suppression.", "error");
      }
    }
  };

  const handleEdit = (category) => {
    setEditMode(true);
    setEditingId(category._id);
    setCategoryName(category.name);
    setShowModal(true);
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true); 
    setImportStatus({ inserted: 0, skipped: 0 });

    try {
      const res = await importCategoriesFromExcel(file);
      const { inserted, skipped } = res.data;

      setImportStatus({ inserted: inserted.length, skipped: skipped.length });

      await fetchCategories(); 
    } catch (err) {
      swal('Erreur', 'Échec de l’import du fichier.', 'error');
      setIsImporting(false);
    } finally {
      setTimeout(() => {
        setIsImporting(false);
        setImportStatus({ inserted: 0, skipped: 0 });
      }, 2000);
    }
  };


useEffect(() => {
  const local = localStorage.getItem(CATEGORY_CACHE_KEY);
  if (local) setCategories(JSON.parse(local));
  fetchCategories(); 
}, []);


  const filtered = categories.filter((cat) =>
    cat.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

return (
    <div className="flex min-h-screen">
      {isRefreshing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <img src="/assets/loading.gif" alt="Chargement" className="mx-auto w-24 h-24 mb-4" />
            <h2 className="text-lg font-bold mb-2">Rafraîchissement en cours...</h2>
            <p className="text-gray-700">Merci de patienter un instant</p>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <img src="/assets/loading.gif" alt="Chargement" className="mx-auto w-16 h-16 mb-4" />
            <h2 className="text-lg font-bold mb-2">Import en cours...</h2>
            <p className="text-gray-700">Ajoutées : <strong>{importStatus.inserted}</strong></p>
            <p className="text-gray-500">Ignorées (doublons) : {importStatus.skipped}</p>
          </div>
        </div>
      )}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <NavbarAdmin slug={slug} />
      </div>

      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>

      <main className="flex-1 md:ml-64 p-6 mt-16 md:mt-0">
        <div className="container mx-auto mt-4">
          {/* Header & Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <h1 className="text-2xl font-bold text-paragaia">Catégories</h1>
            <div className="flex gap-2 items-center relative">
            <button
              onClick={() => fetchCategories(true)}
              className="bg-pink-200 px-4 py-2 rounded hover:bg-pink-300"
            >
              Rafraîchir
            </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setCategoryName('');
                  setShowModal(true);
                }}
                className="bg-paragaia text-white px-4 py-2 rounded hover:bg-paragaiaLight"
              >
                Ajouter
              </button>
              <label
                htmlFor="excel-upload"
                className="cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm md:text-base flex items-center justify-center gap-2 whitespace-nowrap"
                style={{ height: '40px' }}
              >
                <FiUpload className="text-lg" />
                <span className="hidden sm:inline">Import Excel</span>
              </label>
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => {
                  handleExcelImport(e);
                  e.target.value = null;
                }} 
                className="hidden"
              />
            </div>
          </div>

          {/* Search + Pagination Control */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full md:w-1/2 p-3 border rounded"
            />

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="p-2 border rounded"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>
                  Afficher {num}
                </option>
              ))}
            </select>
          </div>

          {/* MODAL */}
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
              <div className="bg-white p-6 rounded-lg w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">
                  {editMode ? "Modifier" : "Ajouter"} une catégorie
                </h2>
                <input
                  type="text"
                  placeholder="Nom de la catégorie"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="w-full mb-4 p-3 border rounded"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setEditMode(false);
                      setCategoryName('');
                      setEditingId(null);
                    }}
                    className="px-4 py-2 border rounded"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="bg-paragaia text-white px-4 py-2 rounded"
                  >
                    {editMode ? "Modifier" : "Ajouter"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TABLE - Desktop */}
          <div className="bg-white rounded-lg shadow overflow-x-auto hidden md:block">
            <table className="w-full text-left text-sm text-gray-700">
              <thead className="bg-paragaia text-white">
                <tr>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Nom</th>
                  <th className="py-3 px-4">Créé le</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((cat, index) => (
                  <tr key={cat._id} className="border-b hover:bg-gray-100">
                    <td className="py-3 px-4">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td className="py-3 px-4">{cat.name}</td>
                    <td className="py-3 px-4">{new Date(cat.createdAt).toLocaleString()}</td>
                    <td className="py-3 px-4 text-center space-x-2">
                      <button
                        onClick={() => handleEdit(cat)}
                        className="px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(cat._id)}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-gray-500">
                      Aucune catégorie trouvée.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* CARD - Mobile */}
          <div className="md:hidden space-y-4">
            {paginated.map((cat, index) => (
              <div key={cat._id} className="bg-white p-4 rounded-lg shadow">
                <p className="font-semibold text-gray-800 mb-1">{cat.name}</p>
                <p className="text-sm text-gray-500 mb-2">
                  Créé le : {new Date(cat.createdAt).toLocaleDateString()}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(cat)}
                    className="px-3 py-1 bg-yellow-500 text-white rounded text-sm"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(cat._id)}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
            {paginated.length === 0 && (
              <p className="text-center text-gray-500 py-6">Aucune catégorie trouvée.</p>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-1 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &lt;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (page === 1 || page === totalPages) return true;
                  if (page >= currentPage - 2 && page <= currentPage + 2) return true;
                  return false;
                })
                .map((page, i, arr) => {
                  const prev = arr[i - 1];
                  const isGap = prev && page - prev > 1;
                  return (
                    <React.Fragment key={page}>
                      {isGap && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${
                          currentPage === page
                            ? 'bg-paragaia text-white'
                            : 'bg-white text-paragaia hover:bg-paragaiaLight'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &gt;
              </button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default CategoriesScreen;